import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import type { Deal } from "~/models/Deal";
import { isOriginVendor } from "~/models/DealVendorCompany";
import type { StaffWithCompany } from "~/models/Staff";
import type { VendorSession } from "~/models/VendorSession";
import type { VendorUserWithCompany } from "~/models/VendorUser";

export type MessageSpeaker =
	| "maintenance_company"
	| "vendor_company"
	| "partner_vendor_company"
	| "system";
export type MessageReceiver =
	| "maintenance_company"
	| "vendor_company"
	| "partner_vendor_company";
export type Message = {
	createdAt: string;
	id: number;
	messageType: "text" | "file";
	partnerReadAt: string | null;
	readAt: string | null;
	speaker: MessageSpeaker;
	staff: StaffWithCompany | null;
	text: string;
	updatedAt: string;
	vendorReadAt: string | null;
	vendorUser: VendorUserWithCompany | null;
};

/**
 * dealとsessionから、受信者としての位置を返す
 */
export const getVendorMessageReceiver = (
	deal: Deal,
	session: VendorSession
): MessageReceiver =>
	isOriginVendor(deal, session.vendorCompany.id)
		? "vendor_company"
		: "partner_vendor_company";

const SYSTEM_SPEAKER_NAME =
	"イタンジ株式会社 修繕おまかせサービス システム通知";

export const getSpeakerName = (
	message: DeepReadonly<Message>
): string | null => {
	if (message.speaker === "system") {
		return SYSTEM_SPEAKER_NAME;
	}
	if (
		["vendor_company", "partner_vendor_company"].includes(message.speaker) &&
		message.vendorUser !== null
	) {
		return `${message.vendorUser.vendorCompany.name} ${message.vendorUser.name}`;
	}
	if (message.speaker === "maintenance_company" && message.staff !== null) {
		return `${message.staff.companySetting.name} ${message.staff.name}`;
	}
	return null;
};

const isRead = (message: Message, receiver: MessageReceiver): boolean => {
	if (receiver === "maintenance_company") {
		// 管理会社は、どちらかが既読したら既読とする
		return message.vendorReadAt !== null || message.partnerReadAt !== null;
	}
	if (receiver === "vendor_company") {
		// 施工会社は、管理会社が既読したら既読とする
		return message.readAt !== null;
	}
	// 実施会社は、管理会社が既読したら既読とする
	return message.readAt !== null;
};

const isUnread = (message: Message, receiver: MessageReceiver): boolean => {
	if (receiver === "maintenance_company") {
		return message.readAt === null;
	}
	if (receiver === "vendor_company") {
		return message.vendorReadAt === null;
	}
	return message.partnerReadAt === null;
};

/**
 * 既読を表示するか否かを返す
 *
 * @param message
 * @param receiver 視点の受信者
 */
export const isShowReadMark = (
	message: DeepReadonly<Message>,
	receiver: MessageReceiver
): boolean => {
	if (message.speaker !== receiver) {
		// 自分の発言以外には既読を付けない
		return false;
	}
	return isRead(message, receiver);
};

export const getUnreadCount = (
	messages: DeepReadonly<Message[]>,
	receiver: MessageReceiver
): number =>
	messages.filter(
		(message: Message) =>
			message.speaker !== receiver &&
			isSystemMessageCountable(message, receiver) &&
			isUnread(message, receiver)
	).length;

/**
 * システムによるメッセージを未読カウントの対象とするかを返す
 *
 * システムによるメッセージは現状管理会社宛のものしか存在しないので、管理会社から見た場合のみ未読カウントの対象とする
 * 将来的に、システムによるメッセージが施工会社や実施会社宛のものも存在するようになった場合、設計を見直す
 */
const isSystemMessageCountable = (
	message: Message,
	receiver: MessageReceiver
): boolean =>
	message.speaker !== "system" || receiver === "maintenance_company";

export const UPDATE_TEMPORARY_UNREAD_MESSAGE =
	"未読ラベルを設定しました。再度チャットにアクセスし、既読するまでは未読として扱われます。なお、チャットの送信者には既読として表示されています。";
